import React from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useCopyToClipboard } from "components/common/useCopyToClipboard";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import type { BankAccount } from "hooks/useGetBankAccounts";

// Escaped rather than literal because DS_MIGRATION.md bans the character in
// source.
const EMPTY = "\u2014";

/** Digits read back more reliably in fours, which is how the design sets them. */
const groupDigits = (value: string) =>
  value
    .replace(/\s+/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim();

interface CopyRowProps {
  label: string;
  value?: string;
  /** Left as typed: an address is prose, not a digit run. */
  group?: boolean;
  /**
   * Whether the reader may see this value at all. A row that holds an account
   * or routing number is Admin-only, and the dialog is the one surface that
   * shows them unmasked.
   */
  sensitive?: boolean;
}

const CopyRow: React.FC<CopyRowProps> = ({
  label,
  value,
  group = false,
  sensitive = false,
}) => {
  const { t } = useTranslation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const withheld = sensitive && !isAdminOrHigher;
  const shown = !value || withheld ? EMPTY : group ? groupDigits(value) : value;

  const copyToClipboard = useCopyToClipboard(t("Copied to clipboard"));

  const copy = () => {
    if (!value || withheld) return;
    copyToClipboard(value);
  };

  return (
    <Inline
      justify="between"
      align="center"
      gap="var(--space-4)"
      wrap={false}
      sx={{ paddingBlock: "var(--space-3)" }}
    >
      <Stack gap="var(--space-0)" sx={{ minWidth: 0 }}>
        <Typography
          variant="label"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {label}
        </Typography>
        <Typography variant="body1">{shown}</Typography>
      </Stack>
      {value && !withheld && (
        <Button
          variant="default-outlined"
          size="small"
          onClick={copy}
          // Every row's button reads "Copy", so the name has to carry the row.
          aria-label={t("Copy {{field}}", { field: label })}
          sx={{ flexShrink: 0 }}
        >
          {t("Copy")}
        </Button>
      )}
    </Inline>
  );
};

interface AccountDetailsDialogProps {
  open: boolean;
  account?: BankAccount;
  onClose: () => void;
}

/**
 * Everything someone needs to send money into this account, in one place they
 * can copy from. Values are shown in full rather than masked: the point of the
 * dialog is to hand them over, and a reveal toggle between the reader and the
 * digits they came for is friction without a purpose.
 */
const AccountDetailsDialog: React.FC<AccountDetailsDialogProps> = ({
  open,
  account,
  onClose,
}) => {
  const { t } = useTranslation();

  const beneficiaryBank = [account?.bankName, account?.bankAddress]
    .filter(Boolean)
    .join(", ");

  const rows = [
    <CopyRow
      key="account"
      label={t("Account number")}
      value={account?.accountNumber}
      group
      sensitive
    />,
    <CopyRow
      key="ach"
      label={t("Routing number (ACH)")}
      value={account?.routingNumber}
      sensitive
    />,
  ];

  const wireRows = [
    <CopyRow
      key="wire-routing"
      label={t("Wire routing number")}
      value={account?.routingNumber}
      sensitive
    />,
    // No SWIFT code exists on the account, so this row says so rather than
    // leaving the reader to assume the list is complete.
    <CopyRow key="swift" label={t("SWIFT / BIC")} />,
    <CopyRow
      key="beneficiary"
      label={t("Beneficiary name")}
      value={account?.accountHolderName}
    />,
    <CopyRow
      key="bank"
      label={t("Beneficiary bank")}
      value={beneficiaryBank || undefined}
    />,
  ];

  const separated = (items: React.ReactElement[]) =>
    items.map((item, index) => (
      <React.Fragment key={item.key}>
        {index > 0 && <Divider />}
        {item}
      </React.Fragment>
    ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("Account details")}</DialogTitle>

      <DialogContent>
        <Stack gap="var(--space-4)">
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {t("AccountDetailsDialogDescription", {
              // Empty string is the case ?? misses, and accounts do arrive
              // with one; the picker and the balance card fall back the same way.
              account: account?.nickname || account?.bankName || t("Account"),
              last4: account?.last4 ?? "",
            })}
          </Typography>

          <Stack gap="var(--space-0)">{separated(rows)}</Stack>

          <Stack gap="var(--space-3)">
            <Typography
              variant="label"
              sx={{
                color: "var(--color-text-tertiary)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {t("Wire instructions")}
            </Typography>
            <Stack gap="var(--space-0)">{separated(wireRows)}</Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="primary-filled" onClick={onClose}>
          {t("Done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountDetailsDialog;
